import { https } from "./https";

export type TimeSlotsParams = {
  partnerProductLocationId: number | string;
  partnerProductId: number | string;
  bookingDate: string; // YYYY-MM-DD
};

export type TimeSlotsResponse = {
  date: string; // YYYY-MM-DD
  slots: string[]; // e.g. "10:15"
};

export async function timeSlots(
  params: TimeSlotsParams,
): Promise<TimeSlotsResponse> {
  const { data } = await https({
    method: "get",
    url: "/order/TimeSlots",
    params,
  });

  return data.data;
}
export type PaymentOrderItemPayload = {
  serviceDate: string;
  serviceTime: string;
  serviceDateTime?: string;
  visitorFirstName: string;
  visitorLastName: string;
  visitorEmailAddress: string;
  visitorPhoneNumber: string;
  hasInsuranceProvider: 0 | 1;
  insuranceProviderName: string;
  insurancePolicyNumber: string;
  partnerProductId: string | number;
  partnerLocationId: string | number;
};

export type PaymentOrderPaymentPayload = {
  provider: "STRIPE" | (string & {});
};

export type OrderStatus = "DRAFT" | "CONFIRMED" | "CANCELLED" | (string & {});
export type PaymentStatus =
  | "PAYMENT_PENDING"
  | "PAID"
  | "FAILED"
  | (string & {});

export type PlaceOrderResponseOrderItem = {
  id: number;
  code: string;
  serviceDateTime: string;
  serviceDate: string | null;
  serviceTime: string | null;
  serviceDuration: number;
  productName: string;
  productQuantity: number;
  productPrice: number;
  platformFee: number;
  serviceTaxAmount: number;
  platformFeeTaxAmount: number;
  payableAmount: number;
  paidAmount: number | null;
  discountAmount: number;
  visitorFirstName: string;
  visitorLastName: string;
  visitorPhoneNumber: string;
  visitorEmailAddress: string;
  hasInsuranceProvider: 0 | 1;
  insuranceProviderName: string;
  insurancePolicyNumber: string;
  status: string;
  orderId: number;
  productId: number;
  partnerLocationId: number;
  isReminderSent: boolean;
  createdAt: string;
  modifiedAt: string;
  trxFee: number;
  trxFeeTax: number;
  trxFeePercentage: number;
  trxFeeTaxPercentage: number;
  platformFeeTaxPercentage: number;
  serviceFeeTaxPercentage: number;
  orderPaymentId: number;
  isVoucherUsed: boolean;
  // Response includes nested `product`, `partnerLocation`, etc.
  [k: string]: unknown;
};

export type PlaceOrderResponseOrderPayment = {
  id: number;
  transactionDate: string;
  provider: string;
  status: string;
  payableAmount: number;
  paidAmount: number | null;
  trxFeePercentage: number;
  trxFee: number;
  method: string;
  payload: string; // JSON string from Stripe
  orderId: number;
  checkoutSessionId: string;
  paymentIntentId: string | null;
  invoiceURL: string | null;
  createdAt: string;
  modifiedAt: string;
  [k: string]: unknown;
};

export type PlaceOrderPayload = {
  isGuestOrder: 0 | 1;
  /** When 1, send booking confirmation emails to visitor email(s). Booker (TPA) and clinic notifications are unaffected. */
  sendVisitorBookingConfirmationEmail?: 0 | 1;
  billingPersonFirstName?: string;
  billingPersonLastName?: string;
  billingPersonContactNumber?: string;
  billingPersonEmail?: string;
  telehealth?: boolean;
  partnerId?: string | number;
  bookerFirstName: string;
  bookerLastName: string;
  order_items: PaymentOrderItemPayload[];
};

export type PlaceOrderResponseOrder = PlaceOrderPayload & {
  id: number;
  code: string;
  payableAmount: number;
  paidAmount: number;
  discountAmount: number;
  orderStatus: OrderStatus;
  paymentStatus: PaymentStatus;
  createdAt: string;
  modifiedAt: string;
  refunds: unknown[];
  adminReviews: unknown[];
  partner?: Record<string, unknown>;
  customer?: Record<string, unknown>;
  [k: string]: unknown;
};

export type StripeCheckoutSession = {
  id: string;
  object: "checkout.session" | (string & {});
  url: string | null;
  payment_status?: "unpaid" | "paid" | "no_payment_required" | (string & {});
  status?: "open" | "complete" | "expired" | (string & {});
  mode?: "payment" | "setup" | "subscription" | (string & {});
  currency?: string | null;
  amount_subtotal?: number | null;
  amount_total?: number | null;
  customer_email?: string | null;
  client_secret?: string | null;
  success_url: string;
  metadata?: Record<string, unknown> | null;
  [k: string]: unknown;
};

export type PlaceOrderResponse = {
  order: PlaceOrderResponseOrder;
  clientSecret?: string;
  stripeCheckOutUrl?: string;
  booking?: {
    userEmail: string;
    description: string[];
  };
  extra: Record<string, unknown>;
  [k: string]: unknown;
};

export async function placeOrder({
  telehealth = false,
  ...rest
}: PlaceOrderPayload): Promise<PlaceOrderResponse> {
  const baseUrl = window.location.origin.includes("localhost:")
    ? "https://customer.dev.health4travel.com"
    : window.location.origin;
  const { data } = await https({
    method: "post",
    url: telehealth ? "/order/telehealth/create" : "/order",
    data: {
      medium: "WEB",
      portal: "agent",
      isAvailableNowOrder: false,
      cancel_url: baseUrl,
      success_url: `${baseUrl}/?payment=success`,
      order_payments: [{ provider: "STRIPE" }],
      ...rest,
    },
  });

  return data.data;
}

export type PrintSuccessOrderPdfParams = {
  telehealth_order: boolean;
  free: boolean;
};

export type PrintSuccessOrderPdfResponse = {
  download_url: string;
  expires_in_seconds: number;
  s3_key: string;
};

export async function printSuccessOrderPdf(
  id: string | number,
  params: PrintSuccessOrderPdfParams,
): Promise<PrintSuccessOrderPdfResponse> {
  const { data } = await https({
    method: "post",
    url: `/order/${id}/confirmation-pdf`,
    params,
  });
  return data.data as PrintSuccessOrderPdfResponse;
}
